<template>
  <p-form-element>
    <p-text-field
      :class="classList"
      type="number"
      role="spinbutton"
      :errors="innerErrors"
      :messages="innerMessages"
      v-bind="$props"
      :value="value"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      :required="innerRequired"
      :readonly="readonly"
      :label="innerLabel"
      :aria-valuenow="value"
      :aria-valuemin="min"
      :aria-valuemax="max"
      @input="onInput"
      @blur="onBlur"
      @keypress.down.prevent="onSubtractClicked"
      @keypress.up.prevent="onAddClicked"
      v-on="$listeners"
    >
      <template v-if="!noSteppers" slot="actions">
        <p-row nowrap no-gutters dense>
          <p-button
            class="number-button"
            dense
            tabindex="-1"
            tertiary
            color="negative"
            :disabled="isSubtractDisabled"
            @click="onSubtractClicked"
          >
            <p-icon
              dense
            >
              remove
            </p-icon>
          </p-button>
          <p-button
            class="number-button"
            dense
            tabindex="-1"
            tertiary
            :disabled="isAddDisabled"
            @click="onAddClicked"
          >
            <p-icon dense>
              add
            </p-icon>
          </p-button>
        </p-row>
      </template>
    </p-text-field>
  </p-form-element>
</template>

<script lang="ts">
  import validatable from '../../mixins/validatable/validatable';
  import mixins from '../../mixins';
  import interactable from '../../mixins/interactable/interactable';
  import PTextField from '../p-text-field/p-text-field.vue';

  export default mixins(interactable, validatable).extend({
    name: 'p-number-field',
    mixins: [interactable, validatable],
    props: {
      ...((new PTextField()).$options.props),
      value: { type: [Number, String], default: undefined },
      step: { type: [Number, String], default: 1 },
      min: { type: [Number, String], default: undefined },
      max: { type: [Number, String], default: undefined },
      noSteppers: { type: Boolean, default: false },
    },
    computed: {
      classList() {
        return {
          'p-number-field': true,
          'has-steppers': !this.noSteppers,
        };
      },
      isAddDisabled() {
        if (this.disabled || this.readonly) {
          return true;
        }
        if (this.value !== undefined && this._.isSet(this.max)) {
          return this.value >= this.max;
        }
        return false;
      },
      isSubtractDisabled() {
        if (this.disabled || this.readonly) {
          return true;
        }
        if (this.value !== undefined && this._.isSet(this.min)) {
          return this.value <= this.min;
        }
        return false;
      },
    },
    methods: {
      filterNonNumber(value: string | number | undefined) {
        if (value === undefined) {
          return undefined;
        }
        let number : number;
        if (typeof (value) === 'string') {
          // Filter out non-digits
          let filteredValue = value.replace(/[^0-9.,-]/g, '');
          filteredValue = filteredValue.replace(/(?!^)-/g, '');
          if (filteredValue === '' || filteredValue === '-') {
            return undefined;
          }
          number = parseFloat(filteredValue);
        } else {
          number = value;
        }
        return number;
      },
      clampToMin(number: number) {
        if (this._.isSet(this.min)) {
          let { min } = this;
          if (typeof min === 'string') {
            min = Number.parseFloat(this.min as string);
          }
          return Math.max(min, number);
        }
        return number;
      },
      clampToMax(number: number) {
        if (this._.isSet(this.max)) {
          let { max } = this;
          if (typeof max === 'string') {
            max = Number.parseFloat(this.min as string);
          }
          return Math.min(max, number);
        }
        return number;
      },
      clampNumber(value: number | undefined): number | undefined {
        if (value === undefined) {
          return undefined;
        }
        let number: number = value;
        number = this.clampToMin(number);
        number = this.clampToMax(number);
        return number;
      },
      onInput(value: string) {
        const number = this.filterNonNumber(value);
        this.$emit('input', number);
      },
      onBlur() {
        let number = this.filterNonNumber(this.value);
        number = this.clampNumber(number);
        this.$emit('input', number);
      },
      addToNumber(number: number | string | undefined, step: number | string = 1, stepFactor: number = 1, initNumber: number = 1) {
        number = this.filterNonNumber(number);
        step = this.filterNonNumber(step) ?? 1;
        step *= stepFactor;
        if (number === undefined) {
          this.$emit('input', this.clampNumber(initNumber));
          return;
        }
        this.$emit('input', this.clampNumber(number + step));
      },
      onAddClicked() {
        this.addToNumber(this.value, this.step, 1, 1);
        this.$emit('add-clicked', true);
      },
      onSubtractClicked() {
        this.addToNumber(this.value, this.step, -1, 0);
        this.$emit('subtract-clicked', true);
      },
    },
  });
</script>
